import React, { useState, useEffect } from 'react';
import { PageProps, Link } from 'gatsby';

import { NavMenu } from './NavMenu';
import { SearchButton } from './SearchButton';
import { SearchResults } from './SearchResults';
import { Logo } from './Logo';
import { PostFooter } from './PostFooter';
import { SideNav, useWindowSize } from './SideNav';
import { Head } from './Head';
import { MDXTemplate } from './MDXTemplate';
import '../styles/index.scss';
import 'typeface-roboto';

interface LayoutProps {
  children: React.ReactNode;
  location: PageProps['location'];
  pageContext: {
    frontmatter?: {
      title?: string;
      visibility?: string;
      description?: string;
    };
  };
}

/**
 * The app's root layout provider
 */
export default function Layout({
  children,
  location,
  pageContext,
}: LayoutProps) {
  const [measured, setMeasured] = useState(false);
  const [showSearch, setSearch] = React.useState(false);
  const [showMenu, setMenu] = React.useState(true);
  const { width } = useWindowSize();

  /**
   *  Make sure SideNav isn't visible on mobile screens for the first render
   */
  useEffect(() => {
    if (width && !measured) {
      if (width < 768) {
        setMenu(false);
      }
      setMeasured(true);
    }
  }, [width, measured]);

  return (
    <>
      <Head
        title={pageContext?.frontmatter?.title}
        description={pageContext?.frontmatter?.description}
        location={location}
      />
      <ul className="skiplinks">
        <li>
          <a href="#search-btn">Skip to Search</a>
        </li>
        <li>
          <a href="#main">Skip to Content</a>
        </li>
        <li>
          <a href="#side-nav">Skip to Side Navigation</a>
        </li>
      </ul>
      <header>
        <nav className="flex align-center">
          <div className="nav-logo">
            <Link to="/">
              <Logo />
            </Link>
          </div>
          <div className="nav-content flex align-center">
            <div className="nav-search">
              <SearchButton toggleSearch={() => setSearch(true)} />
            </div>
            <div className="primary-menu">
              <NavMenu />
              <button id="btn-mobile-menu" onClick={() => setMenu(!showMenu)}>
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
                </svg>
              </button>
            </div>
          </div>
        </nav>
      </header>
      <div
        id="site-inner"
        style={!measured ? { visibility: 'hidden' } : undefined}>
        <SideNav
          pathname={location.pathname}
          show={showMenu}
          set={setMenu}
          width={width}
        />
        <main id="main">
          {pageContext?.frontmatter ? (
            <MDXTemplate path={location.pathname}>{children}</MDXTemplate>
          ) : (
            children
          )}
          <PostFooter />
        </main>
      </div>
      <SearchResults show={showSearch} set={setSearch} />
    </>
  );
}
